import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { SealCheck, ArrowRight, IdentificationCard, ShieldCheck, Clock, XCircle } from "@phosphor-icons/react";

const PRICE = 299;

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
};

export default function CandidateVerifiedBadge() {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/me/verified-badge/status")
        .then((r) => setState(r.data))
        .catch(() => setState({ badge: { status: "none" }, kyc: {}, order: null, price_inr: PRICE }));
    useEffect(() => { load(); }, []);

    const badgeStatus = state?.badge?.status || "none";
    const orderStatus = state?.order?.status;
    const kyc = state?.kyc || {};
    const kycUploaded = !!(kyc.aadhar || kyc.pan);
    // V1.5.8.16 — tier-aware upsell. `tier` = 'paid' | 'kyc' | null.
    const tier = state?.tier || null;
    const kycAlreadyVerified = tier === "kyc";

    const pay = async () => {
        setBusy(true);
        try {
            const rzOk = await new Promise((resolve) => {
                if (window.Razorpay) return resolve(true);
                const s = document.createElement("script");
                s.src = "https://checkout.razorpay.com/v1/checkout.js";
                s.onload = () => resolve(true);
                s.onerror = () => resolve(false);
                document.body.appendChild(s);
            });
            if (!rzOk) throw new Error("Couldn't load payment gateway");
            const { data: order } = await api.post("/me/verified-badge/checkout");
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy",
                description: "Verified Badge · ₹299",
                theme: { color: "#1E3AEF" },
                handler: async (rp) => {
                    try {
                        await api.post("/me/verified-badge/verify", {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        toast.success("Payment received. Complete KYC to submit for review.");
                        load();
                    } catch (err) {
                        toast.error("Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBusy(false) },
            });
            rz.open();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start checkout");
        } finally { setBusy(false); }
    };

    const submitForReview = async () => {
        setBusy(true);
        try {
            await api.post("/me/verified-badge/submit-for-review");
            toast.success("Submitted for review. We'll notify you within 48 hours.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't submit");
        } finally { setBusy(false); }
    };

    if (!state) return <div className="p-10 text-[#525252]">Loading…</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="verified-badge-page">
            <div className="mb-8">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-2">
                    Trust · Priority · Recruiter attention
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Get the blue checkmark.
                </h1>
                <p className="text-sm sm:text-base text-[#525252] mt-3 max-w-xl">
                    One-time <b>₹{PRICE}</b>. Recruiters filter for verified profiles first —
                    verified candidates get <b>3.2× more views</b> on average.
                </p>
            </div>

            {/* V1.5.8.16 — Tier-aware upsell: shown to candidates who are
                KYC-verified (free tier) but haven't paid for the ₹299 blue-check.
                Positioned above the buy CTA so it becomes the primary hook. */}
            {kycAlreadyVerified && badgeStatus === "none" && (
                <div
                    data-testid="upsell-kyc-to-paid"
                    className="bg-gradient-to-br from-emerald-50 via-white to-cobalt/5 border border-emerald-200 p-5 sm:p-6 mb-6 relative overflow-hidden"
                >
                    <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-emerald-200/40 blur-2xl" aria-hidden />
                    <div className="relative flex items-start gap-3">
                        <SealCheck size={28} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 mb-1">
                                You&apos;re KYC-verified — level up
                            </p>
                            <h3 className="font-heading text-xl sm:text-2xl font-black tracking-tight text-ink leading-tight">
                                Want to rank <span className="text-cobalt">above</span> them?
                            </h3>
                            <p className="text-sm text-[#525252] mt-2 leading-relaxed">
                                Recruiters see KYC-verified candidates alongside every other AI-verified profile.
                                <b> Paid-Verified badges rank first in agency shortlists</b>, get the blue-check on your
                                public page, and unlock the &quot;Verified&quot; filter that agencies use to shortlist first.
                            </p>
                            <button
                                onClick={pay}
                                disabled={busy}
                                data-testid="upsell-upgrade-btn"
                                className="mt-4 btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {busy ? "Loading…" : (<>Upgrade to Paid Verified — ₹{PRICE} <ArrowRight size={13} weight="bold" /></>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* V1.5.8.16 — Unverified candidates: nudge to do the FREE KYC step
                first, then the paid upgrade. Softer than the buy CTA below. */}
            {tier === null && badgeStatus === "none" && !kycUploaded && (
                <div
                    data-testid="upsell-none-to-kyc"
                    className="bg-cobalt/5 border border-cobalt/20 p-5 mb-6"
                >
                    <div className="flex items-start gap-3">
                        <IdentificationCard size={24} weight="fill" className="text-cobalt mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                                Free step — takes 60 seconds
                            </p>
                            <h3 className="font-heading text-lg sm:text-xl font-black tracking-tight text-ink">
                                Get KYC-verified first — it&apos;s free.
                            </h3>
                            <p className="text-sm text-[#525252] mt-1.5">
                                Upload Aadhaar or PAN, and our AI verifies it in seconds. Your profile immediately shows up under &quot;Verified only&quot; on the recruiter marketplace with an emerald KYC chip.
                            </p>
                            <Link
                                to="/candidate/profile"
                                data-testid="upsell-kyc-start-btn"
                                className="mt-3 inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-cobalt hover:underline"
                            >
                                Upload KYC now <ArrowRight size={12} weight="bold" />
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Status card */}
            {badgeStatus === "verified" && (
                <div data-testid="badge-status-verified"
                     className="bg-emerald-50 border-2 border-emerald-500 p-6 mb-6">
                    <div className="flex items-center gap-3">
                        <SealCheck size={40} weight="fill" className="text-emerald-600" />
                        <div>
                            <h2 className="font-heading text-2xl font-black text-emerald-700">You&apos;re verified.</h2>
                            <p className="text-xs text-emerald-800/80 mt-0.5">
                                Approved on {fmtDate(state.badge?.approved_at)} by {state.badge?.decided_by || "admin"}.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {badgeStatus === "pending_review" && (
                <div data-testid="badge-status-pending" className="bg-amber-50 border border-amber-300 p-5 mb-6">
                    <div className="flex items-start gap-3">
                        <Clock size={22} weight="fill" className="text-amber-700 mt-0.5" />
                        <div>
                            <p className="font-heading font-black text-amber-800">Under review</p>
                            <p className="text-xs text-amber-800/80 mt-1">
                                Your documents are with our compliance team. Expect a decision within 48 hours.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {badgeStatus === "rejected" && (
                <div data-testid="badge-status-rejected" className="bg-red-50 border border-red-300 p-5 mb-6">
                    <div className="flex items-start gap-3">
                        <XCircle size={22} weight="fill" className="text-red-700 mt-0.5" />
                        <div className="flex-1">
                            <p className="font-heading font-black text-red-800">Verification declined</p>
                            {state.badge?.decision_reason && (
                                <p className="text-xs text-red-800 mt-1">
                                    Reason: {state.badge.decision_reason}
                                </p>
                            )}
                            <p className="text-xs text-red-800/80 mt-2">
                                You can retry with correct documents.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {(badgeStatus === "awaiting_kyc" || orderStatus === "paid") && (
                <div data-testid="badge-status-awaiting-kyc" className="bg-cobalt/5 border border-cobalt/30 p-5 mb-6">
                    <div className="flex items-start gap-3">
                        <IdentificationCard size={22} weight="fill" className="text-cobalt mt-0.5" />
                        <div className="flex-1">
                            <p className="font-heading font-black text-ink">Payment received — upload KYC</p>
                            <p className="text-xs text-[#525252] mt-1">
                                Upload your Aadhaar OR PAN on the Profile page, then click &quot;Submit for review&quot; below.
                            </p>
                            <div className="flex gap-2 mt-3">
                                <Link to="/candidate/profile" data-testid="badge-go-kyc"
                                      className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-cobalt hover:underline">
                                    Upload KYC <ArrowRight size={12} weight="bold" />
                                </Link>
                                {kycUploaded && (
                                    <button onClick={submitForReview} disabled={busy}
                                            data-testid="badge-submit-review"
                                            className="btn-gradient px-4 py-2 text-xs font-black uppercase tracking-wider disabled:opacity-60">
                                        Submit for review
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Benefits + CTA */}
            {(badgeStatus === "none" || badgeStatus === "rejected") && (
                <div className="bg-white border border-[#E5E5E5] p-6 sm:p-8">
                    <h2 className="font-heading text-xl font-black text-ink mb-4">What you get</h2>
                    <ul className="space-y-3 mb-6">
                        {[
                            "Blue checkmark on your profile — recruiters see you first",
                            "Priority listing when recruiters filter by verified candidates",
                            "Trust signal that speeds up shortlisting decisions",
                            "One-time payment — badge never expires",
                        ].map((t, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-[#404040]">
                                <ShieldCheck size={18} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                                {t}
                            </li>
                        ))}
                    </ul>
                    <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-[#F5F5F5]">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">One-time</p>
                            <p className="font-heading text-3xl font-black text-ink">₹{PRICE}</p>
                        </div>
                        <button onClick={pay} disabled={busy}
                                data-testid="badge-buy-btn"
                                className="btn-gradient px-6 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
                            {busy ? "Loading…" : (<>Get verified <ArrowRight size={14} weight="bold" /></>)}
                        </button>
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] mt-4">
                        Refundable within 24 hours if you haven&apos;t uploaded KYC yet. Contact support@naukridaddy.com.
                    </p>
                </div>
            )}
        </div>
    );
}
